import { CsvCell } from './csv'

export const DISTRIBUTION_TOTAL_LABEL = '__total__'
export const DISTRIBUTION_OTHER_LABEL = '__other__'

const PERCENTAGE_DECIMALS = 4
const PERCENTAGE_SCALE = 10n ** BigInt(PERCENTAGE_DECIMALS)

export type DistributionExportRow = {
  dimensionValue?: string
  count: number | string
  percentage?: number
  secondary?: Record<string, string | number | null | undefined>
}

export type DistributionCsvInput = {
  rows: DistributionExportRow[]
  secondaryColumns?: string[]
  totalCount?: number | string
}

function toBigInt(value: number | string): bigint {
  if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) {
    return BigInt(value.trim())
  }

  const numeric = Number(value)
  return Number.isFinite(numeric) ? BigInt(Math.trunc(numeric)) : 0n
}

function derivePercentage(count: bigint, total: bigint): number {
  if (total === 0n) {
    return 0
  }

  return Number((count * 200n * PERCENTAGE_SCALE + total) / (2n * total)) / Number(PERCENTAGE_SCALE)
}

export function percentageOf(count: number | string, total: number | string): number {
  return derivePercentage(toBigInt(count), toBigInt(total))
}

function round(value: number): number {
  return Number(value.toFixed(PERCENTAGE_DECIMALS))
}

export function buildDistributionCsvRows({
  rows,
  secondaryColumns = [],
  totalCount,
}: DistributionCsvInput): CsvCell[][] {
  const counts = rows.map((row) => toBigInt(row.count))
  const listedTotal = counts.reduce((acc, count) => acc + count, 0n)
  const expectedTotal = totalCount != null ? toBigInt(totalCount) : 0n
  // Approximate counts can overshoot the grand total; never export a negative remainder.
  const total = expectedTotal > listedTotal ? expectedTotal : listedTotal
  const otherCount = total - listedTotal

  const header: CsvCell[] = ['dimension_value', 'count', 'percentage_of_total', ...secondaryColumns]
  const emptySecondary = secondaryColumns.map(() => '')

  let percentageSum = 0

  const dataRows = rows.map((row, index): CsvCell[] => {
    const percentage = row.percentage != null ? round(row.percentage * 100) : derivePercentage(counts[index], total)
    percentageSum += percentage

    return [
      row.dimensionValue ?? '',
      counts[index].toString(),
      percentage,
      ...secondaryColumns.map((column) => row.secondary?.[column] ?? ''),
    ]
  })

  const hasExplicitShares = rows.some((row) => row.percentage != null)
  const otherPercentage = hasExplicitShares
    ? Math.max(0, round(100 - percentageSum))
    : derivePercentage(otherCount, total)
  const otherRows: CsvCell[][] =
    otherCount > 0n ? [[DISTRIBUTION_OTHER_LABEL, otherCount.toString(), otherPercentage, ...emptySecondary]] : []

  const totalRow: CsvCell[] = [DISTRIBUTION_TOTAL_LABEL, total.toString(), total === 0n ? 0 : 100, ...emptySecondary]

  return [header, ...dataRows, ...otherRows, totalRow]
}
